import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash, FaSave, FaTimes, FaPlus } from "react-icons/fa";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

export default function ProjectCRUD() {
  const [projects, setProjects] = useState([]);
  const [newProject, setNewProject] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const navigate = useNavigate();

  // ✅ Fetch all projects
  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/projects`);
      const data = await res.json();
      setProjects(data);
    } catch (error) {
      toast.error("Failed to load projects.");
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  // ✅ Authentication check
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) {
      toast.error("You must be logged in to access this page.");
      navigate("/");
    }
  }, [navigate]);

  // ✅ Add new project
  const handleAdd = async () => {
    if (!newProject.trim()) {
      toast.warning("Please enter a project name.");
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newProject }),
      });
      if (res.ok) {
        toast.success("Project added successfully!");
        setNewProject("");
        fetchProjects();
      } else {
        const msg = await res.text();
        toast.error(msg);
      }
    } catch {
      toast.error("Error adding project.");
    }
  };

  // ✅ Update project
  const handleUpdate = async (id) => {
    if (!editName.trim()) {
      toast.warning("Enter a valid project name.");
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/projects/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: editName }),
      });
      if (res.ok) {
        toast.success("Project updated!");
        setEditingId(null);
        fetchProjects();
      } else {
        const msg = await res.text();
        toast.error(msg);
      }
    } catch {
      toast.error("Error updating project.");
    }
  };

  // ✅ Delete project
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/projects/${id}`, {
        method: "DELETE",
      });
      if (res.ok) {
        toast.info("Project deleted.");
        fetchProjects();
      } else {
        const msg = await res.text();
        toast.error(msg);
      }
    } catch {
      toast.error("Error deleting project.");
    }
  };

  return (
    <>
      {/* Fixed Navbar */}
      <div className="fixed-top">
        <AdminNavbar />
      </div>

      {/* Full-page layout */}
      <div
        className="d-flex flex-column p-4"
        style={{
          minHeight: "100vh",
          backgroundColor: "#F5F7FB",
          marginTop: "110px",
        }}
      >
        <h3 className="fw-bold text-dark mb-4 text-start">
          Project Management
        </h3>

        <div className="row flex-grow-1 g-4">
          {/* Left Column - Add Project */}
          <div className="col-md-4 d-flex">
            <div
              className="card shadow-sm border-0 flex-fill"
              style={{
                borderRadius: "12px",
                height: "fit-content",
                alignSelf: "flex-start",
              }}
            >
              <div
                className="card-header fw-semibold"
                style={{
                  backgroundColor: "#DFE4EB",
                  color: "black",
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px",
                }}
              >
                Add New Project
              </div>
              <div className="card-body">
                <input
                  type="text"
                  className="form-control mb-3"
                  placeholder="Enter project name"
                  value={newProject}
                  onChange={(e) => setNewProject(e.target.value)}
                />
                <button
                  className="btn text-dark w-40"
                  style={{ backgroundColor: "#DFE4EB" }}
                  onClick={handleAdd}
                >
                  <FaPlus className="me-2" />
                  Add Project
                </button>
              </div>
            </div>
          </div>

          {/* Right Column - Project List */}
          <div className="col-md-8 d-flex">
            <div
              className="card shadow-sm border-0 flex-fill"
              style={{ borderRadius: "12px" }}
            >
              <div
                className="card-header fw-semibold d-flex justify-content-between align-items-center"
                style={{
                  backgroundColor: "#DFE4EB",
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px",
                }}
              >
                <span>All Projects</span>
                <span className="badge bg-secondary">
                  {projects.length} Total
                </span>
              </div>

              <div
                className="card-body p-0"
                style={{ maxHeight: "70vh", overflowY: "auto" }}
              >
                <ul className="list-group list-group-flush">
                  {projects.length === 0 && (
                    <li className="list-group-item text-muted text-center py-3">
                      No projects found.
                    </li>
                  )}
                  {projects.map((p) => (
                    <li
                      key={p.id}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      {editingId === p.id ? (
                        <input
                          type="text"
                          className="form-control me-2"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          style={{ maxWidth: "300px" }}
                        />
                      ) : (
                        <span className="fw-medium">{p.name}</span>
                      )}

                      <div>
                        {editingId === p.id ? (
                          <>
                            <button
                              className="btn btn-success btn-sm me-2"
                              onClick={() => handleUpdate(p.id)}
                            >
                              <FaSave /> Save
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => setEditingId(null)}
                            >
                              <FaTimes /> Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="btn  btn-sm me-2"
                              onClick={() => {
                                setEditingId(p.id);
                                setEditName(p.name);
                              }}
                            >
                              <FaEdit /> 
                            </button>
                            <button
                              className="btn  btn-sm"
                              onClick={() => handleDelete(p.id)}
                            >
                              <FaTrash /> 
                            </button>
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
